import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Injector,
  booleanAttribute,
  computed,
  forwardRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'tc-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  template: `
    @if (label()) {
      <label class="tc-input-label" [attr.for]="id() || null">
        {{ label() }}
        @if (isRequired()) {
          <span class="tc-required-mark" aria-hidden="true">*</span>
        }
      </label>
    }
    <input
      class="tc-input-control flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-sm text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      [id]="id()"
      [type]="type()"
      [placeholder]="placeholder()"
      [disabled]="isDisabled()"
      [required]="isRequired()"
      [attr.aria-required]="isRequired() ? 'true' : null"
      [value]="value()"
      (input)="onInput($event)"
      (blur)="onTouched()"
    />
  `,
  styles: `
    :host {
      display: block;
    }

    :host(.tc-input--labeled) {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .tc-input-label {
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--foreground);
    }
  `,
  host: {
    '[attr.data-required]': 'isRequired() ? "true" : null',
    '[class.tc-input--required]': 'isRequired()',
    '[class.tc-input--labeled]': '!!label()',
  },
})
export class InputComponent implements ControlValueAccessor, AfterViewInit {
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly id = input<string | undefined>(undefined);
  readonly type = input<'text' | 'email' | 'password' | 'number'>('text');
  readonly placeholder = input('');
  readonly label = input<string>('');
  /** Force required marker; when omitted, auto-detects Validators.required on the bound control. */
  readonly required = input(false, { transform: booleanAttribute });
  readonly textInput = output<string>();

  protected readonly value = signal('');
  protected readonly isDisabled = signal(false);
  private readonly requiredFromControl = signal(false);

  readonly isRequired = computed(
    () => this.required() || this.requiredFromControl(),
  );

  private onChange: (value: string) => void = () => undefined;
  private onTouchedCallback: () => void = () => undefined;

  ngAfterViewInit(): void {
    const ngControl = this.injector.get(NgControl, null, {
      self: true,
      optional: true,
    });
    const control = ngControl?.control;
    if (!control) return;

    const syncRequired = () => {
      this.requiredFromControl.set(control.hasValidator(Validators.required));
      this.cdr.markForCheck();
    };
    syncRequired();
    control.statusChanges
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => syncRequired());
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected onInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this.value.set(next);
    this.onChange(next);
    this.textInput.emit(next);
  }

  protected onTouched(): void {
    this.onTouchedCallback();
  }
}
